export interface AnchoredPosition {
    /** Anchored by `top` when opening downward, or by `bottom` when flipped upward. */
    top?: number
    bottom?: number
    left: number
    width: number
    /** Capped to the available space on the chosen side so the popover never runs off-screen. */
    maxHeight: number
}

/**
 * Position a portal-rendered popover against an anchor rect: prefer opening below, flip above only
 * when below can't fit `desiredMax` and above has more room, and cap height to whichever space was
 * actually chosen. The cap must never exceed the real available space — inflating it past that (e.g.
 * a floor like `Math.max(140, available)`) is what makes a popover spill past the viewport edge near
 * the top/bottom, defeating the whole point of capping it.
 */
export const computeAnchoredPosition = (rect: DOMRect, opts: { desiredMax: number; width?: number; gap?: number; margin?: number }): AnchoredPosition => {
    const gap = opts.gap ?? 4
    const margin = opts.margin ?? 8
    const width = opts.width ?? rect.width
    const spaceBelow = window.innerHeight - rect.bottom - gap - margin
    const spaceAbove = rect.top - gap - margin
    const openUp = spaceBelow < opts.desiredMax && spaceAbove > spaceBelow
    const available = openUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(0, Math.min(opts.desiredMax, available))

    return openUp
        ? { bottom: window.innerHeight - rect.top + gap, left: rect.left, width, maxHeight }
        : { top: rect.bottom + gap, left: rect.left, width, maxHeight }
}
